<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import {
  acquireMobileSheetScrollLock,
  releaseMobileSheetScrollLock,
} from '@/services/mobileSheetScrollLockService'
import { mobileSheetFullHeight, mobileSheetMaxHeight } from '@/services/safeAreaService'
import { useUiStore } from '@/stores/uiStore'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'MobileActionSheet' })

type SheetSnap = 'medium' | 'high'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    titleByline?: string | null
    dotColor?: string | null
    keyboardOpenLiftPx?: number
    preferredSnap?: SheetSnap
    mediumHeightPx?: number
    highHeightPx?: number
    preserveBottomPanelState?: boolean
    allowHighSwipeThroughDismiss?: boolean
  }>(),
  { preferredSnap: 'medium' },
)

const emit = defineEmits<{
  close: []
  closed: []
}>()

const uiStore = useUiStore()
const isRendered = ref(false)
const isClosing = ref(false)
const isEntering = ref(false)
const dragY = ref(0)
const activeSnap = ref<SheetSnap>('medium')
const viewportHeight = ref(typeof window === 'undefined' ? 800 : window.innerHeight)
const keyboardShiftY = ref(0)
const suppressNextClick = ref(false)
const suppressOpeningClick = ref(false)
const scrollContainerRef = ref<HTMLElement | null>(null)
let closeTimer: ReturnType<typeof setTimeout> | null = null
let enterTimer: ReturnType<typeof setTimeout> | null = null
let openingClickTimer: ReturnType<typeof setTimeout> | null = null
let keyboardClearanceFrame: number | null = null
let keyboardClearanceSettleFrame: number | null = null
let focusedField: HTMLElement | null = null
let scrollLockHeld = false
let dragStartY = 0
let dragStartTime = 0
let dragging = false
let dragStartedInScrollableContent = false

const KEYBOARD_DETECTION_THRESHOLD_PX = 120
const KEYBOARD_FIELD_GAP_PX = 8
const SNAP_GESTURE_THRESHOLD_PX = 60
const DISMISS_GESTURE_THRESHOLD_PX = 120
const DIRECT_DISMISS_VELOCITY_PX_PER_MS = 1.4
const DIRECT_DISMISS_MIN_DISTANCE_PX = 128
const HIGH_SNAP_TOP_GAP_PX = 8

const mediumSheetHeight = computed(() =>
  Math.max(320, props.mediumHeightPx ?? uiStore.mobileBottomPanelHeight),
)
const highSheetHeight = computed(() =>
  Math.max(
    mediumSheetHeight.value,
    props.highHeightPx ?? mobileSheetFullHeight(viewportHeight.value, HIGH_SNAP_TOP_GAP_PX),
  ),
)
const displayedSheetHeight = computed(() => {
  const medium = mediumSheetHeight.value
  const high = highSheetHeight.value
  if (activeSnap.value === 'medium' && dragY.value < 0) {
    return Math.min(high, medium + Math.abs(dragY.value))
  }
  if (activeSnap.value === 'high' && dragY.value > 0) {
    return Math.max(medium, high - dragY.value)
  }
  return activeSnap.value === 'high' ? high : medium
})
const highToMediumDistance = computed(() => highSheetHeight.value - mediumSheetHeight.value)
const dragPastMedium = computed(() =>
  activeSnap.value === 'high'
    ? props.allowHighSwipeThroughDismiss
      ? Math.max(0, dragY.value - highToMediumDistance.value)
      : 0
    : Math.max(0, dragY.value),
)

const sheetStyle = computed(() => {
  const style: Record<string, string> = {
    height: `${displayedSheetHeight.value}px`,
    maxHeight: mobileSheetMaxHeight(),
    paddingBottom: 'max(env(safe-area-inset-bottom), 8px)',
    transition: dragY.value === 0 ? 'height 0.22s ease, transform 0.22s ease' : 'none',
  }

  if (isClosing.value) {
    style.transform = 'translateY(100%)'
    style.transition = 'height 0.22s ease, transform 0.22s ease'
  } else if (dragPastMedium.value > 0) {
    style.transform = `translateY(${dragPastMedium.value - keyboardShiftY.value}px)`
    style.transition = 'none'
  } else if (keyboardShiftY.value !== 0) {
    style.transform = `translateY(${-keyboardShiftY.value}px)`
    style.transition = 'none'
  }

  return style
})

const clearCloseTimer = () => {
  if (closeTimer !== null) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
}

const clearEnterTimer = () => {
  if (enterTimer !== null) {
    clearTimeout(enterTimer)
    enterTimer = null
  }
}

const clearOpeningClickTimer = () => {
  if (openingClickTimer !== null) {
    clearTimeout(openingClickTimer)
    openingClickTimer = null
  }
}

const requestExpandedHeight = () => {
  window.dispatchEvent(new CustomEvent('mobile-bottom-panel-expanded-height'))
  uiStore.expandMobileBottomPanel()
}

watch(
  () => props.open,
  (open) => {
    clearCloseTimer()

    if (open) {
      const startsEnterAnimation = !isRendered.value
      viewportHeight.value = window.innerHeight
      if (!props.preserveBottomPanelState) requestExpandedHeight()
      if (!scrollLockHeld) {
        acquireMobileSheetScrollLock()
        scrollLockHeld = true
      }
      focusedField = null
      isRendered.value = true
      isClosing.value = false
      isEntering.value = startsEnterAnimation
      activeSnap.value = props.preferredSnap
      dragY.value = 0
      void nextTick(() => {
        if (scrollContainerRef.value) scrollContainerRef.value.scrollTop = 0
      })
      clearEnterTimer()
      if (startsEnterAnimation) {
        enterTimer = setTimeout(() => finishSheetEnter(), 220)
      }
      suppressOpeningClick.value = true
      clearOpeningClickTimer()
      openingClickTimer = setTimeout(() => {
        suppressOpeningClick.value = false
        openingClickTimer = null
      }, 300)
      return
    }

    if (!isRendered.value) return
    focusedField = null
    keyboardShiftY.value = 0
    isEntering.value = false
    clearEnterTimer()
    isClosing.value = true
    closeTimer = setTimeout(() => {
      isRendered.value = false
      isClosing.value = false
      dragY.value = 0
      if (scrollLockHeld) {
        releaseMobileSheetScrollLock()
        scrollLockHeld = false
      }
      closeTimer = null
      emit('closed')
    }, 220)
  },
  { immediate: true },
)

watch(
  () => props.preferredSnap,
  (snap) => {
    if (props.open) activeSnap.value = snap
  },
)

const requestClose = () => {
  emit('close')
}

const isFocusField = (target: EventTarget | null): target is HTMLElement =>
  target instanceof HTMLInputElement ||
  target instanceof HTMLTextAreaElement ||
  target instanceof HTMLSelectElement ||
  (target instanceof HTMLElement && target.isContentEditable)

const ensureFocusedFieldKeyboardClearance = () => {
  keyboardClearanceFrame = null
  const field = focusedField
  const viewport = window.visualViewport
  if (!field || document.activeElement !== field || !viewport) return

  const keyboardInset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
  if (keyboardInset <= KEYBOARD_DETECTION_THRESHOLD_PX) {
    keyboardShiftY.value = 0
    return
  }

  if (props.keyboardOpenLiftPx !== undefined) {
    keyboardShiftY.value = props.keyboardOpenLiftPx
    return
  }

  const keyboardTop = viewport.offsetTop + viewport.height
  const overlap = field.getBoundingClientRect().bottom + KEYBOARD_FIELD_GAP_PX - keyboardTop
  keyboardShiftY.value += overlap
}

const scheduleKeyboardClearance = () => {
  if (isEntering.value) return
  if (keyboardClearanceFrame !== null) cancelAnimationFrame(keyboardClearanceFrame)
  if (keyboardClearanceSettleFrame !== null) cancelAnimationFrame(keyboardClearanceSettleFrame)
  keyboardClearanceFrame = requestAnimationFrame(() => {
    keyboardClearanceFrame = null
    keyboardClearanceSettleFrame = requestAnimationFrame(() => {
      keyboardClearanceSettleFrame = null
      // Safari emits visualViewport events throughout its keyboard animation. Waiting for a second
      // paint frame coalesces that burst and ensures the previous sheet transform is reflected in
      // getBoundingClientRect before applying another correction.
      ensureFocusedFieldKeyboardClearance()
    })
  })
}

function finishSheetEnter() {
  if (!isEntering.value) return
  isEntering.value = false
  clearEnterTimer()
  if (focusedField) scheduleKeyboardClearance()
}

const onPanelAnimationEnd = (event: AnimationEvent) => {
  if (event.target !== event.currentTarget) return
  finishSheetEnter()
}

const onPanelFocusIn = (event: FocusEvent) => {
  if (!isFocusField(event.target)) return
  focusedField = event.target
  scheduleKeyboardClearance()
}

const onPanelFocusOut = (event: FocusEvent) => {
  if (event.target !== focusedField) return
  focusedField = null
}

const onPanelPointerDownCapture = (event: PointerEvent) => {
  const field = event.target
  if (!isFocusField(field) || document.activeElement === field) return

  // Native iOS focus may pan the visual viewport before our keyboard-clearance logic runs. Replace
  // that default action with the same non-scrolling programmatic focus used on initial sheet open.
  // Calling focus inside the trusted pointer event still allows the software keyboard to appear.
  event.preventDefault()
  field.focus({ preventScroll: true })
}

const onVisualViewportChange = () => {
  if (focusedField) {
    scheduleKeyboardClearance()
    return
  }
  const viewport = window.visualViewport
  const keyboardInset = viewport
    ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
    : 0
  if (keyboardInset <= KEYBOARD_DETECTION_THRESHOLD_PX) keyboardShiftY.value = 0
}

const onDragStart = (event: TouchEvent) => {
  if (event.touches.length !== 1) return
  const target = event.target
  const startedInContent =
    target instanceof Element && target.closest('[data-mobile-action-sheet-scroll]') !== null
  if (startedInContent && (scrollContainerRef.value?.scrollTop ?? 0) > 0) {
    dragging = false
    dragStartedInScrollableContent = false
    dragY.value = 0
    return
  }
  dragStartY = event.touches[0].clientY
  dragStartTime = Date.now()
  dragging = true
  dragStartedInScrollableContent = startedInContent
  dragY.value = 0
  suppressNextClick.value = false
}

const onDragMove = (event: TouchEvent) => {
  if (!dragging || event.touches.length !== 1) return
  const deltaY = event.touches[0].clientY - dragStartY
  if (dragStartedInScrollableContent && (scrollContainerRef.value?.scrollTop ?? 0) > 0) {
    dragging = false
    dragStartedInScrollableContent = false
    dragY.value = 0
    return
  }
  if (dragStartedInScrollableContent && activeSnap.value === 'high' && deltaY < 0) {
    dragging = false
    dragStartedInScrollableContent = false
    dragY.value = 0
    return
  }
  dragY.value = deltaY
  if (Math.abs(dragY.value) > 8) {
    suppressNextClick.value = true
  }
  if (event.cancelable) event.preventDefault()
}

const onDragEnd = () => {
  const dragDuration = Math.max(1, Date.now() - dragStartTime)
  const downwardVelocity = dragY.value > 0 ? dragY.value / dragDuration : 0
  const startedAtHigh = activeSnap.value === 'high'
  dragging = false
  dragStartedInScrollableContent = false

  if (
    props.allowHighSwipeThroughDismiss &&
    startedAtHigh &&
    dragY.value >= DIRECT_DISMISS_MIN_DISTANCE_PX &&
    downwardVelocity >= DIRECT_DISMISS_VELOCITY_PX_PER_MS
  ) {
    dragY.value = 0
    requestClose()
    return
  }
  if (
    props.allowHighSwipeThroughDismiss &&
    startedAtHigh &&
    dragPastMedium.value > DISMISS_GESTURE_THRESHOLD_PX
  ) {
    dragY.value = 0
    requestClose()
    return
  }
  if (startedAtHigh && dragY.value > SNAP_GESTURE_THRESHOLD_PX) {
    activeSnap.value = 'medium'
    dragY.value = 0
    return
  }
  if (activeSnap.value === 'medium' && dragY.value < -SNAP_GESTURE_THRESHOLD_PX) {
    activeSnap.value = 'high'
    dragY.value = 0
    return
  }
  if (activeSnap.value === 'medium' && dragY.value > DISMISS_GESTURE_THRESHOLD_PX) {
    dragY.value = 0
    requestClose()
    return
  }
  dragY.value = 0
}

const onPanelClickCapture = (event: MouseEvent) => {
  if (!suppressNextClick.value && !suppressOpeningClick.value) return
  event.preventDefault()
  event.stopImmediatePropagation()
  suppressNextClick.value = false
  suppressOpeningClick.value = false
  clearOpeningClickTimer()
}

onBeforeUnmount(() => {
  clearCloseTimer()
  clearEnterTimer()
  clearOpeningClickTimer()
  if (keyboardClearanceFrame !== null) cancelAnimationFrame(keyboardClearanceFrame)
  if (keyboardClearanceSettleFrame !== null) cancelAnimationFrame(keyboardClearanceSettleFrame)
  if (scrollLockHeld) releaseMobileSheetScrollLock()
  window.visualViewport?.removeEventListener('resize', onVisualViewportChange)
  window.visualViewport?.removeEventListener('scroll', onVisualViewportChange)
})

onMounted(() => {
  window.visualViewport?.addEventListener('resize', onVisualViewportChange)
  window.visualViewport?.addEventListener('scroll', onVisualViewportChange)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isRendered"
      class="fixed inset-0 flex flex-col justify-end"
      :style="{ zIndex: Z_INDEX.MOBILE_ACTION_SHEET }"
      @click.self="requestClose"
    >
      <div
        class="relative flex flex-col overflow-hidden rounded-t-2xl border-t border-gray-200/70 bg-white/96 shadow-[0_-8px_30px_rgba(15,23,42,0.14)] backdrop-blur-xl mobile-action-sheet-panel"
        :style="sheetStyle"
        @click.capture="onPanelClickCapture"
        @click.stop
        @focusin="onPanelFocusIn"
        @focusout="onPanelFocusOut"
        @pointerdown.capture="onPanelPointerDownCapture"
        @animationend="onPanelAnimationEnd"
        @touchstart.passive="onDragStart"
        @touchmove="onDragMove"
        @touchend="onDragEnd"
        @touchcancel="onDragEnd"
      >
        <div
          class="flex shrink-0 cursor-grab justify-center pb-1.5 pt-3"
          data-mobile-action-sheet-drag-handle
        >
          <div class="h-1.5 w-10 rounded-full bg-gray-300" />
        </div>

        <div class="relative shrink-0 border-b border-gray-100/90 px-4 pb-3 pt-1.5">
          <div class="absolute left-3 top-0 flex h-9 items-center">
            <slot name="header-left" />
          </div>
          <div class="absolute right-3 top-0 flex h-9 items-center">
            <slot name="header-right" />
          </div>
          <div class="flex min-w-0 items-center justify-center gap-1.5 px-12">
            <slot name="title">
              <span
                v-if="dotColor"
                class="h-2 w-2 shrink-0 rounded-full"
                :style="{ backgroundColor: dotColor }"
              ></span>
              <span class="min-w-0 truncate text-base font-semibold text-gray-900">{{
                title
              }}</span>
              <span
                v-if="titleByline"
                class="shrink-0 text-xs font-normal text-gray-500"
                data-testid="mobile-action-sheet-title-byline"
              >
                {{ titleByline }}
              </span>
            </slot>
          </div>
        </div>

        <div
          ref="scrollContainerRef"
          class="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain py-1"
          data-mobile-action-sheet-scroll
        >
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.mobile-action-sheet-panel {
  animation: mobile-action-sheet-enter 0.18s ease-out;
}

@keyframes mobile-action-sheet-enter {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
